import { Box, Typography, Grid, Paper, alpha } from "@mui/material";
import { PeopleOutline } from "@mui/icons-material";
import ParticipantCard from "./ParticipantCard";
import { IDelegate } from "../../../interfaces/IDelegate";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApiClient } from "../../../ApiClientProvider";
import { PublicEventService } from "../../../services/publicEventService";
import { useNotifications } from "@toolpad/core";

const PAGE_SIZE = 12;

export default function EventAttendees({
    eventId,
    onCardClick,
    primaryColor = '#493193',
    secondaryColor = '#2fadd8',
}: {
    eventId: string | undefined;
    onCardClick?: (a: IDelegate) => void;
    primaryColor?: string;
    secondaryColor?: string;
}) {

    const [attendees, setAttendees] = useState<IDelegate[]>([])
    //console.log(attendees)
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const apiClient = useApiClient("applicationClient");
    let endpoint = "event";
    if (location.pathname.startsWith("/events/web")) {
        endpoint = "events/web";
    }
    const eventService = new PublicEventService(apiClient, endpoint);
    const notifications = useNotifications();

    // Infinite scroll refs
    const isFetchingRef = useRef(false);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastAttendeeRef = useRef<HTMLDivElement | null>(null);

    const buildRequest = (pageNum: number) => ({
        page: pageNum,
        pageSize: PAGE_SIZE
    });

    const fetchData = useCallback(
        async (reset = false) => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            setLoading(true);
            try {
                const req = buildRequest(reset ? 1 : page);
                const result = await eventService.getEventAttendees(eventId ?? "", req);
                // Remove duplicates by id
                const items = Array.isArray(result?.items)
                    ? result.items.filter((item, index, self) => self.findIndex(i => i.id === item.id) === index)
                    : [];
                setHasMore(items.length === PAGE_SIZE);
                if (reset || page === 1) {
                    setAttendees(items);
                } else {
                    setAttendees((prev) => [...prev, ...items]);
                }
            } catch {
                notifications.show("Error loading Attendees", {
                    autoHideDuration: 3000,
                    severity: "error"
                })
                setHasMore(false);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        },
        [eventService, page]
    );

    useEffect(() => {
        setPage(1);
        fetchData(true);
    }, [eventId]);

    useEffect(() => {
        if (page > 1) fetchData();
    }, [page]);

    useEffect(() => {
        if (loading || !hasMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new window.IntersectionObserver((entries) => {
            if (
                entries[0].isIntersecting &&
                !loading &&
                hasMore &&
                !isFetchingRef.current
            ) {
                setPage((prev) => prev + 1);
            }
        });

        if (lastAttendeeRef.current) {
            observer.current.observe(lastAttendeeRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [attendees, loading, hasMore]);

    return (
        <Box sx={{ mb: 2 }}>
            <Paper
                elevation={2}
                sx={{
                    p: { xs: 2, md: 4 },
                    mb: { xs: 3, md: 4 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(primaryColor, 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
                }}
            >
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        mb: 1,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Attendees
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    See who else is attending this event.
                </Typography>
            </Paper>
            <Paper
                elevation={2}
                sx={{
                    p: { xs: 2, md: 4 },
                    mb: { xs: 3, md: 4 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(primaryColor, 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
                }}
            >
                {!loading && attendees.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 220,
                            px: { xs: 2, md: 4 },
                            py: { xs: 4, md: 6 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            borderRadius: 2.5,
                            border: `1px dashed ${alpha(primaryColor, 0.18)}`,
                            background: `linear-gradient(180deg, ${alpha(primaryColor, 0.04)} 0%, ${alpha(secondaryColor, 0.05)} 100%)`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                mb: 2,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: alpha(primaryColor, 0.1),
                                color: primaryColor,
                            }}
                        >
                            <PeopleOutline sx={{ fontSize: 30 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            No attendees to show yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, lineHeight: 1.7 }}>
                            Attendee profiles will appear here once registrations are visible for this event.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={4} justifyContent="flex-start" alignItems="stretch">
                        {attendees.map((attendee, idx) => (
                            <Grid
                                size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
                                key={attendee.id}
                                ref={idx === attendees.length - 1 ? lastAttendeeRef : undefined}
                            >
                                <ParticipantCard
                                    participant={attendee}
                                    onClick={onCardClick}
                                    primaryColor={primaryColor}
                                    secondaryColor={secondaryColor}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>
        </Box>
    );
}